import { lazy } from 'react'
import { telemetry } from './telemetry'

/**
 * Wrap React.lazy(() => import('...')) com retry + reload em falha de
 * chunk-load.
 *
 * Cenario tipico: user ficou com aba aberta, fizemos deploy com chunks
 * de hash diferente, ai ao navegar pra rota lazy o browser tenta baixar
 * o JS antigo (nao existe mais no servidor) -> 404 -> "Failed to fetch
 * dynamically imported module".
 *
 * Estrategia:
 * 1. Primeira falha: aguarda 400ms e tenta de novo (resolve blip de rede).
 * 2. Segunda falha: marca uma flag em sessionStorage e da reload na
 *    pagina. Reload busca o novo index.html que referencia os novos
 *    chunks. Flag impede loop infinito caso o problema seja outro.
 */
export function lazyWithRetry(importFn) {
  return lazy(async () => {
    try {
      return await importFn()
    } catch (firstError) {
      try {
        await new Promise((r) => setTimeout(r, 400))
        return await importFn()
      } catch (secondError) {
        const reloadFlag = 'lazy-chunk-reloaded'
        const alreadyReloaded = sessionStorage.getItem(reloadFlag) === '1'
        telemetry.captureException(secondError, {
          scope: 'lazyWithRetry',
          alreadyReloaded,
          firstError: firstError?.message,
        })
        if (!alreadyReloaded && typeof window !== 'undefined') {
          sessionStorage.setItem(reloadFlag, '1')
          window.location.reload()
          // Devolve um modulo "vazio" pra Suspense nao quebrar antes do reload
          return { default: () => null }
        }
        throw secondError
      }
    }
  })
}

/**
 * Limpa a flag de reload quando a app carrega com sucesso, pra futuras
 * falhas terem direito a um reload tambem.
 */
export function clearLazyReloadFlag() {
  try {
    sessionStorage.removeItem('lazy-chunk-reloaded')
  } catch {
    // ignora
  }
}
